import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { stateKey } from "../documents/useDocuments";
import { DEFAULT_CO_RATES, CO_SERVICE_LABELS, summarize } from "../co/selection";
import { CO_ORDER_STATUSES, CO_ORDER_STATUS_LABELS, type CoDeliverable, type CoOrderStatus } from "../co/status";
import { CO_DELIVERABLE_POINTS, CO_TERMS, CO_TERMS_VERSION } from "../co/terms";
import { useCoSelection, type CoOrderView } from "../co/useCoSelection";
import { useCurrentRoomUser } from "../hooks/useCurrentRoomUser";
import { CO_SERVICES, type CoService } from "../schema";
import { ds, dsCardSx } from "../theme/ds";
import { CalloutBox } from "../widgets/live/Callout";

import { BasePage } from "./BasePage";

import type { PageTemplateProps } from "./types";

// Checkout (Content Optimization room, 2026-09-23, Eric): the cart from Your
// assortment priced by service, the terms with one acknowledgment tick, the
// order button, and every order placed before. Placing an order is a POST to
// /api/rooms/{slug}/orders; the server decides the cells (cart minus what is
// already ordered), records the order, clears the cart, emails both sides
// and posts the staff card. Settlement is a discount on a future invoice.
// Since 2026-10-02 each placed order shows its fulfilment status; Bison staff
// change it in place (rooms-co-order-status), and moving it to "Deliverables
// sent" asks for the links the supplier receives.
export function COCheckout(props: PageTemplateProps) {
    const { room } = props;
    const rates = { ...DEFAULT_CO_RATES, ...(room.contentOptimization?.rates ?? {}) };
    const cart = useCoSelection(room.slug);
    const [agreed, setAgreed] = useState(false);
    const [placed, setPlaced] = useState<CoOrderView | null>(null);
    const client = useQueryClient();
    const { pathname } = useLocation();
    const assortmentHref = pathname.replace(/\/[^/]+$/, "/your-assortment");

    // Only cells not already on an order are for sale.
    const open: Record<string, CoService[]> = {};
    for (const [asin, services] of Object.entries(cart.selection)) {
        const keep = services.filter((s) => !cart.locked.has(`${asin}:${s}`));
        if (keep.length) open[asin] = keep;
    }
    const summary = summarize(open, rates, new Set((room.contentOptimization?.assortment ?? []).map((r) => r.asin)));
    const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

    const place = useMutation({
        mutationFn: async () => {
            const res = await fetch(`/api/rooms/${room.slug}/orders`, {
                method: "POST",
                credentials: "include",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ acceptTerms: true, termsVersion: CO_TERMS_VERSION }),
            });
            const data = (await res.json().catch(() => ({}))) as { error?: string; coOrders?: CoOrderView[] };
            if (!res.ok) throw new Error(data.error ?? `order failed: ${res.status}`);
            return data;
        },
        onSuccess: (data) => {
            client.setQueryData(stateKey(room.slug), data);
            setPlaced(data.coOrders?.[data.coOrders.length - 1] ?? null);
            setAgreed(false);
        },
    });

    const orders = [...cart.orders].reverse();
    const { user } = useCurrentRoomUser();
    // The order waiting for its deliverable links before it moves to "delivered".
    const [delivering, setDelivering] = useState<CoOrderView | null>(null);
    const [links, setLinks] = useState("");

    const setStatus = useMutation({
        mutationFn: async ({ id, status, deliverables }: { id: string; status: CoOrderStatus; deliverables?: CoDeliverable[] }) => {
            const res = await fetch(`/api/rooms/${room.slug}/co-orders/${id}/status`, {
                method: "PUT",
                credentials: "include",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ status, deliverables }),
            });
            const data = (await res.json().catch(() => ({}))) as { error?: string };
            if (!res.ok) throw new Error(data.error ?? `status change failed: ${res.status}`);
            return data;
        },
        onSuccess: (data) => {
            client.setQueryData(stateKey(room.slug), data);
            setDelivering(null);
            setLinks("");
        },
    });
    // One link per line, optionally "Label | https://...".
    const parsedLinks: CoDeliverable[] = links
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
            const i = l.lastIndexOf("|");
            return i > 0 ? { label: l.slice(0, i).trim(), url: l.slice(i + 1).trim() } : { url: l };
        });

    // The proposal report's column (1040px, 32px gutter), like Our capabilities (Eric, 2026-09-24).
    return (
        <BasePage {...props} maxWidth={1040}>
            {placed && (
                <CalloutBox
                    variant="info"
                    title={`Order placed, ${money(placed.total)}`}
                    body={`We emailed a confirmation to ${placed.by}. We deliver in about 3 weeks once we have all the product information we need, and we will contact you if anything is missing. The ordered cells now show a blue tick on Your assortment.`}
                />
            )}

            <Box sx={dsCardSx}>
                <Typography sx={{ fontSize: ds.text.lg, fontWeight: 700, color: ds.navy, mb: 1.5 }}>What you receive</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {CO_DELIVERABLE_POINTS.map((p, i) => (
                        <Typography component="li" key={i} sx={{ fontSize: ds.text.base, lineHeight: 1.65, color: ds.gray600, mb: 0.5 }}>
                            {p}
                        </Typography>
                    ))}
                </Box>
            </Box>

            {summary.cells === 0 ? (
                <Box sx={dsCardSx}>
                    <Typography sx={{ fontSize: ds.text.lg, fontWeight: 700, color: ds.navy, mb: 1 }}>Nothing in your cart yet</Typography>
                    <Typography sx={{ fontSize: ds.text.base, color: ds.gray600, mb: 2 }}>
                        Tick the services you want on the Your assortment page, or apply the slider to select every weak
                        score at once.
                    </Typography>
                    <Button component={RouterLink} to={assortmentHref} variant="contained">
                        Go to Your assortment
                    </Button>
                </Box>
            ) : (
                <>
                    <Box sx={{ ...dsCardSx, p: 0, overflow: "hidden" }}>
                        <Table size="small" sx={{ "& th": { bgcolor: ds.navy, color: ds.white, fontSize: ds.text.xs, textTransform: "uppercase", fontWeight: 600, border: 0 }, "& td": { fontSize: ds.text.base, color: ds.gray600, borderColor: ds.gray100 } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Service</TableCell>
                                    <TableCell align="right">ASINs</TableCell>
                                    <TableCell align="right">Per ASIN</TableCell>
                                    <TableCell align="right">Subtotal</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {CO_SERVICES.filter((s) => summary.byService[s] > 0).map((s) => (
                                    <TableRow key={s}>
                                        <TableCell sx={{ color: ds.navy, fontWeight: 600 }}>{CO_SERVICE_LABELS[s]}</TableCell>
                                        <TableCell align="right">{summary.byService[s]}</TableCell>
                                        <TableCell align="right">{money(rates[s])}</TableCell>
                                        <TableCell align="right">{money(summary.byService[s] * rates[s])}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell sx={{ color: ds.navy, fontWeight: 800, fontSize: ds.text.lg, borderBottom: 0 }}>Total</TableCell>
                                    <TableCell align="right" sx={{ borderBottom: 0 }}>{summary.asins} ASINs</TableCell>
                                    <TableCell sx={{ borderBottom: 0 }} />
                                    <TableCell align="right" sx={{ color: ds.navy, fontWeight: 800, fontSize: ds.text.lg, borderBottom: 0 }}>{money(summary.total)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>
                    <Typography sx={{ fontSize: ds.text.sm, color: ds.gray500 }}>
                        {summary.cells === 1 ? "One service" : `${summary.cells} services`} across {summary.asins} ASINs.{" "}
                        <RouterLink to={assortmentHref} style={{ color: ds.info.text }}>
                            Change the selection
                        </RouterLink>
                        .
                    </Typography>

                    <Box sx={dsCardSx}>
                        <Typography sx={{ fontSize: ds.text.lg, fontWeight: 700, color: ds.navy, mb: 1.5 }}>Service terms</Typography>
                        <Stack spacing={1.5} sx={{ mb: 2 }}>
                            {CO_TERMS.map((p, i) => (
                                <Typography key={i} sx={{ fontSize: ds.text.base, lineHeight: 1.65, color: ds.gray600 }}>
                                    {p}
                                </Typography>
                            ))}
                        </Stack>
                        <FormControlLabel
                            control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} sx={{ "&.Mui-checked": { color: ds.navy } }} />}
                            label={`I accept these terms on behalf of ${room.supplier.supplierName}.`}
                            sx={{ "& .MuiFormControlLabel-label": { fontSize: ds.text.base, color: ds.navy } }}
                        />
                        <Stack direction="row" spacing={2} sx={{ alignItems: "center", mt: 2 }}>
                            <Button variant="contained" size="large" disabled={!agreed || place.isPending} onClick={() => place.mutate()}>
                                {place.isPending ? "Placing the order" : `Place order, ${money(summary.total)}`}
                            </Button>
                            {place.error && (
                                <Typography sx={{ fontSize: ds.text.sm, color: ds.danger.text }}>{(place.error as Error).message}</Typography>
                            )}
                        </Stack>
                    </Box>
                </>
            )}

            {orders.length > 0 && (
                <Box sx={{ ...dsCardSx, p: 0, overflow: "hidden" }}>
                    <Typography sx={{ fontSize: ds.text.lg, fontWeight: 700, color: ds.navy, p: "20px 20px 8px" }}>Orders placed</Typography>
                    <Table size="small" sx={{ "& th": { fontSize: ds.text.xs, textTransform: "uppercase", color: ds.gray500, borderColor: ds.gray100 }, "& td": { fontSize: ds.text.base, color: ds.gray600, borderColor: ds.gray100 } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Placed</TableCell>
                                <TableCell>By</TableCell>
                                <TableCell align="right">ASINs</TableCell>
                                <TableCell align="right">Services</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((o) => (
                                <TableRow key={o.id}>
                                    <TableCell>{new Date(o.at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</TableCell>
                                    <TableCell>{o.byName ? `${o.byName} (${o.by})` : o.by}</TableCell>
                                    <TableCell align="right">{o.asins}</TableCell>
                                    <TableCell align="right">{o.services}</TableCell>
                                    <TableCell align="right" sx={{ color: ds.navy, fontWeight: 600 }}>{money(o.total)}</TableCell>
                                    <TableCell>
                                        {user?.staff ? (
                                            <Select
                                                size="small"
                                                value={o.status ?? "placed"}
                                                disabled={setStatus.isPending}
                                                onChange={(e) => {
                                                    const status = e.target.value as CoOrderStatus;
                                                    if (status === "delivered") {
                                                        setLinks((o.deliverables ?? []).map((d) => (d.label ? `${d.label} | ${d.url}` : d.url)).join("\n"));
                                                        setDelivering(o);
                                                    } else setStatus.mutate({ id: o.id, status });
                                                }}
                                                sx={{ fontSize: ds.text.sm, minWidth: 190 }}
                                            >
                                                {CO_ORDER_STATUSES.map((s) => (
                                                    <MenuItem key={s} value={s} sx={{ fontSize: ds.text.sm }}>
                                                        {CO_ORDER_STATUS_LABELS[s]}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        ) : (
                                            CO_ORDER_STATUS_LABELS[o.status ?? "placed"]
                                        )}
                                        {o.deliverables && o.deliverables.length > 0 && (
                                            <Stack spacing={0.25} sx={{ mt: 0.75 }}>
                                                {o.deliverables.map((d) => (
                                                    <a key={d.url} href={d.url} target="_blank" rel="noreferrer" style={{ color: ds.info.text, fontSize: ds.text.sm }}>
                                                        {d.label || d.url}
                                                    </a>
                                                ))}
                                            </Stack>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
            {setStatus.error && !delivering && (
                <Typography sx={{ fontSize: ds.text.sm, color: ds.danger.text }}>{(setStatus.error as Error).message}</Typography>
            )}

            <Dialog open={delivering !== null} onClose={() => setDelivering(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ color: ds.navy, fontWeight: 700 }}>Send the deliverables</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: ds.text.base, color: ds.gray600, mb: 2 }}>
                        Paste the links to the Excel or CSV file and the asset folders, one per line. To label a link, put the label
                        and a | before it, like this: Content file | https://... The supplier who placed the order gets an email
                        with these links.
                    </Typography>
                    <TextField multiline minRows={4} fullWidth value={links} onChange={(e) => setLinks(e.target.value)} />
                    {setStatus.error && (
                        <Typography sx={{ fontSize: ds.text.sm, color: ds.danger.text, mt: 1 }}>{(setStatus.error as Error).message}</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDelivering(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={parsedLinks.length === 0 || setStatus.isPending}
                        onClick={() => delivering && setStatus.mutate({ id: delivering.id, status: "delivered", deliverables: parsedLinks })}
                    >
                        {setStatus.isPending ? "Sending" : "Send to the supplier"}
                    </Button>
                </DialogActions>
            </Dialog>
        </BasePage>
    );
}
